import { DatasetName } from "./constants.ts";
import { ChartTypeName } from "./report-configuration.ts";
import { FIELD_BASES, type ColumnName, type FieldName } from "./fields.ts";

export type MeasureV2 = {
  datasetNames: DatasetName[];
  measure: MeasureName;
  label: string;
  columnsReferenced: Array<ColumnName | "NOT_IMPLEMENTED">; // List of fields that this measure references, used for resolving field-level permissions.
  // "NOT_IMPLEMENTED" is a placeholder for measures that have not yet been implemented and should be hidden from the UI.
  // Note that the fields listed are not necessarily used in the query, for example Current Stage (converted into actual column names in the sql builder).
  group: string;
  axisGroup: AxisGroup; // Determines whether this measure can be grouped with other measures on the same Y axis
  alwaysShowCount: boolean; // Whether to always show value_num (i.e. value_perc should be hidden and is not used)
  showEmployeeCount: boolean; // Whether employee count should be shown in tooltips (non-projected data only)
  requiresActiveEmployeeFilter: boolean; // Some measures require an active employee filter to be applied
  requiresTimeDimensionOrFilter: boolean; // Whether the measure requires a time slice of some kind, either a single period or a series
  rollupForLLM: boolean; // whether to calculate and send a rollup of this measure when formatting query results for LLM consumption
  isTransactional?: boolean; // Whether this measure is transactional and should not be aggregated in certain ways (uses an alternate path in the SQL builder)
  description: string; // Description used for AI prompts
  axisLabelOverrideCount?: string; // When showCount=true and this is present, will be used in the getAxisLabels function
  axisLabelOverridePercent?: string; // When showCount=false and this is present, will be used in the getAxisLabels function
  disableDimensions?: boolean; // Attrition drivers and similar measures should not have dimensions applied
  disableProjections?: boolean; // Some measures should not have projections applied
  preferChartType?: ChartTypeName; // If present, this chart type will be forced when the measure is selected
};

export type MeasureName =
  | "ACTIONS_TAKEN"
  | "APPLICANT_COUNT"
  | "ATTRITION_DRIVERS"
  | "ATTRITION_PROBABILITY"
  | "ATTRITION_RATE"
  | "ATTRITION_RATE_BY_SEGMENT"
  | "AVG_ANNUALIZED_TOTAL_CASH_COMPENSATION"
  | "AVG_BONUS_AMOUNT"
  | "AVG_COMMISSION_AMOUNT"
  | "AVG_COMPENSATION"
  | "AVG_COMPLETION_VELOCITY_DAYS"
  | "AVG_COMPLETION_PERCENTAGE"
  | "AVG_INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY"
  | "AVG_INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION"
  | "AVG_INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS"
  | "AVG_INDUSTRY_SEPARATION_INTENSITY"
  | "AVG_INDUSTRY_TOTAL_POSITIONS"
  | "AVG_INSIGHTS_CONSCIOUS_BLUE_ENERGY"
  | "AVG_INSIGHTS_CONSCIOUS_RED_ENERGY"
  | "AVG_INSIGHTS_CONSCIOUS_GREEN_ENERGY"
  | "AVG_INSIGHTS_CONSCIOUS_YELLOW_ENERGY"
  | "AVG_INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY"
  | "AVG_INSIGHTS_LESS_CONSCIOUS_RED_ENERGY"
  | "AVG_INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY"
  | "AVG_INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY"
  | "AVG_LAG_TIME_DAYS"
  | "AVG_LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO"
  | "AVG_LABOR_AVAILABILITY_EMPLOYED_WORKERS"
  | "AVG_LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE"
  | "AVG_LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY"
  | "AVG_LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO"
  | "AVG_LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS"
  | "AVG_LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO"
  | "AVG_LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS"
  | "AVG_LABOR_AVAILABILITY_UNEMPLOYED_WORKERS"
  | "AVG_MARKET_PRESSURE_ROLE"
  | "AVG_MARKET_PRESSURE_MARKET"
  | "AVG_OCCUPATION_MARKET_EMP_CHANGE_YOY"
  | "AVG_OCCUPATION_MARKET_GROWTH_OPENINGS"
  | "AVG_OCCUPATION_MARKET_HIRES"
  | "AVG_OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM"
  | "AVG_OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE"
  | "AVG_OCCUPATION_MARKET_REPLACEMENT_OPENINGS"
  | "AVG_OCCUPATION_MARKET_RESIDENT_EMPLOYMENT"
  | "AVG_OCCUPATION_MARKET_SEPARATIONS"
  | "AVG_OCCUPATION_MARKET_YOY_HIRES_CHANGE"
  | "AVG_OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE"
  | "AVG_OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE"
  | "AVG_OCCUPATION_LOCAL_HIRE_RATE"
  | "AVG_OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE"
  | "AVG_OCCUPATION_NATIONAL_HIRE_RATE"
  | "AVG_OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE"
  | "AVG_OCCUPATION_OCCUPIED_POSITIONS"
  | "AVG_OCCUPATION_SEPARATION_INTENSITY"
  | "AVG_PERFORMANCE_RATING"
  | "AVG_POSITIONS_IN_INDUSTRY_AND_OCCUPATION"
  | "AVG_SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT"
  | "AVG_SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE"
  | "AVG_SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY"
  | "AVG_SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS"
  | "AVG_SURVEY_SCORE"
  | "AVG_TIME_TO_FILL"
  | "BENCHMARK_HEADCOUNT"
  | "COMPANY_COUNT"
  | "COMPENSATION_GAP"
  | "COMPENSATION_RANGE_TOP"
  | "COMPENSATION_RANGE_BOTTOM"
  | "COST_OF_ATTRITION"
  | "GOAL_COUNT"
  | "HEADCOUNT"
  | "HEADCOUNT_GOALS"
  | "HIRES"
  | "HIRES_FROM_APPLICATIONS"
  | "INCIDENT_COUNT"
  | "JOB_REQ_ACCEPTANCE_RATE"
  | "JOB_REQ_COUNT"
  | "PROMOTIONS"
  | "RETENTION_RATE"
  | "SURVEY_SCORE"
  | "TERMINATIONS"
  | "TIME_IN_STAGE"
  | "TIME_TO_FILL_DRIVERS"
  | "TIME_TO_HIRE_DAYS"
  | "TOTAL_ANNUALIZED_TOTAL_CASH_COMPENSATION"
  | "TOTAL_BONUS_AMOUNT"
  | "TOTAL_COMMISSION_AMOUNT"
  | "TOTAL_COMPENSATION"
  | "TOTAL_LABOR_AVAILABILITY_EMPLOYED_WORKERS"
  | "TOTAL_LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS"
  | "TOTAL_LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS"
  | "TOTAL_LABOR_AVAILABILITY_UNEMPLOYED_WORKERS"
  | "TOTAL_OCCUPATION_MARKET_RESIDENT_EMPLOYMENT"
  | "TOTAL_SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE"
  | "TOTAL_SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS"
  | "TOTAL_INSIGHTS_CONSCIOUS_BLUE_ENERGY"
  | "TOTAL_INSIGHTS_CONSCIOUS_RED_ENERGY"
  | "TOTAL_INSIGHTS_CONSCIOUS_GREEN_ENERGY"
  | "TOTAL_INSIGHTS_CONSCIOUS_YELLOW_ENERGY"
  | "TOTAL_INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY"
  | "TOTAL_INSIGHTS_LESS_CONSCIOUS_RED_ENERGY"
  | "TOTAL_INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY"
  | "TOTAL_INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY";

export type AxisGroup =
  | "actions_taken"
  | "applicant_count"
  | "avg_compensation"
  | "benchmark_percentage"
  | "color_energy"
  | "company_count"
  | "compensation_gap"
  | "cost_of_attrition"
  | "fraction_percentage"
  | "generic_percentage"
  | "goal_count"
  | "headcount"
  | "hires"
  | "importance"
  | "incident_count"
  | "intensity"
  | "intensity10"
  | "job_req_count"
  | "market_count"
  | "number_of_respondents"
  | "performance_rating"
  | "probability"
  | "promotions"
  | "survey_score"
  | "terminations"
  | "time_in_days"
  | "total_compensation";

export const AXIS_GROUP_LABELS: Record<AxisGroup, string> = {
  actions_taken: "ER Case Count",
  applicant_count: "Number of Applicants",
  avg_compensation: "Compensation",
  benchmark_percentage: "Percentage",
  color_energy: "Insights Energy Score",
  compensation_gap: "Compensation Gap (%)",
  company_count: "Company Count",
  cost_of_attrition: "Cost of Attrition",
  fraction_percentage: "Percentage",
  generic_percentage: "THIS WILL BE REPLACED IN THE LABEL LOGIC",
  headcount: "Number of Employees",
  hires: "Hires",
  intensity: "Intensity",
  intensity10: "Index (0-10)",
  importance: "Importance Score",
  incident_count: "Number of Incidents",
  job_req_count: "Number of Job Requisitions",
  market_count: "Market Count",
  goal_count: "Number of Goals",
  number_of_respondents: "Number of Respondents",
  performance_rating: "Performance Rating",
  probability: "Probability",
  promotions: "Promotions",
  survey_score: "Average Score",
  terminations: "Terminations",
  time_in_days: "Time (Days)",
  total_compensation: "Total Compensation",
};

const createLightcastMeasure = (
  measure: MeasureName,
  fieldName: FieldName,
  aggregation: "average" | "total",
  axisGroup: AxisGroup,
  description: string,
): MeasureV2 => {
  const field = FIELD_BASES[fieldName];
  const labelPrefix = aggregation === "average" ? "Avg." : "Total";

  return {
    datasetNames: ["lightcast"],
    measure,
    label: `${labelPrefix} ${field.label}`,
    columnsReferenced: [field.field],
    group: "Industry",
    axisGroup,
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    disableProjections: true,
    description: `${description} This measure ${aggregation === "average" ? "averages" : "sums"} source rows.`,
  };
};

export const MEASURES: Record<MeasureName, MeasureV2> = {
  ACTIONS_TAKEN: {
    datasetNames: ["employee_relations"],
    measure: "ACTIONS_TAKEN",
    label: "ER Case Count",
    columnsReferenced: ["Action Taken"],
    group: "Employee Relations",
    axisGroup: "actions_taken",
    alwaysShowCount: false,
    showEmployeeCount: false, // Action counts, not employee counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the number of disciplinary actions taken within the organization.",
  },
  APPLICANT_COUNT: {
    datasetNames: ["recruitment_applications"],
    measure: "APPLICANT_COUNT",
    label: "Applicant Count",
    columnsReferenced: ["Application ID"],
    group: "Recruitment",
    axisGroup: "applicant_count",
    alwaysShowCount: true,
    showEmployeeCount: false, // Applicant counts, not employee counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Sums the number of applicants in the recruitment pipeline including demographic data and application date",
  },
  ATTRITION_DRIVERS: {
    datasetNames: ["attrition_drivers"],
    measure: "ATTRITION_DRIVERS",
    label: "Attrition Drivers",
    columnsReferenced: ["Importance"],
    group: "Organization",
    axisGroup: "importance",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the importance score of various factors contributing to employee attrition on a scale of 0 to 1.",
    disableDimensions: true,
    disableProjections: true,
  },
  ATTRITION_PROBABILITY: {
    datasetNames: ["main"],
    measure: "ATTRITION_PROBABILITY",
    label: "Attrition Probability",
    columnsReferenced: ["attrition_6m"],
    group: "Organization",
    axisGroup: "probability",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the likelihood of employees leaving the organization, represented as a probability value between 0 and 1.",
  },
  ATTRITION_RATE: {
    datasetNames: ["main"],
    measure: "ATTRITION_RATE",
    label: "Company-relative Attrition Rate",
    columnsReferenced: ["terminationDate"],
    group: "Organization",
    axisGroup: "generic_percentage",
    alwaysShowCount: true,
    showEmployeeCount: false, // Rate metrics have distorted employee counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: true,
    rollupForLLM: false,
    description:
      "Measures the percentage of employees who leave the organization over a specified time period, indicating turnover levels.",
    axisLabelOverrideCount: "Attrition Rate (%)",
  },
  ATTRITION_RATE_BY_SEGMENT: {
    datasetNames: ["main"],
    measure: "ATTRITION_RATE_BY_SEGMENT",
    label: "Cohort-level Attrition Rate",
    columnsReferenced: ["terminationDate"],
    group: "Organization",
    axisGroup: "generic_percentage",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: true,
    rollupForLLM: false,
    description:
      "Measures the percentage of employees within chosen dimensions who leave the organization over a specified time period.",
    axisLabelOverrideCount: "Attrition Rate (%)",
  },
  AVG_ANNUALIZED_TOTAL_CASH_COMPENSATION: {
    datasetNames: ["main"],
    measure: "AVG_ANNUALIZED_TOTAL_CASH_COMPENSATION",
    label: "Avg. Total Cash Compensation",
    columnsReferenced: ["annualized_total_cash_compensation"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average total cash compensation of employees within the organization, including salary and bonuses.",
  },
  AVG_BONUS_AMOUNT: {
    datasetNames: ["main"],
    measure: "AVG_BONUS_AMOUNT",
    label: "Avg. Bonus Amount",
    columnsReferenced: ["bonusAmount"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    isTransactional: true,
    description:
      "Measures the average bonus amount received by employees within the organization, providing insights into variable compensation trends.",
  },
  AVG_COMMISSION_AMOUNT: {
    datasetNames: ["main"],
    measure: "AVG_COMMISSION_AMOUNT",
    label: "Avg. Commission Amount",
    columnsReferenced: ["commissionAmount"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    isTransactional: true,
    description:
      "Measures the average commission amount received by employees within the organization, providing insights into variable compensation trends for sales roles.",
  },
  AVG_COMPENSATION: {
    datasetNames: ["main"],
    measure: "AVG_COMPENSATION",
    label: "Avg. Compensation",
    columnsReferenced: ["total_compensation"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average total compensation of employees within the organization, including salary and additional benefits.",
  },
  AVG_COMPLETION_VELOCITY_DAYS: {
    datasetNames: ["employee_goals"],
    measure: "AVG_COMPLETION_VELOCITY_DAYS",
    label: "Avg. Completion Velocity (Days)",
    columnsReferenced: ["completionVelocity_days"],
    group: "Employee Goals",
    axisGroup: "time_in_days",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average completion velocity of employee goals in days.",
  },
  AVG_COMPLETION_PERCENTAGE: {
    datasetNames: ["employee_goals"],
    measure: "AVG_COMPLETION_PERCENTAGE",
    label: "Avg. Completion Percentage",
    columnsReferenced: ["employeeGoalCompletionPercent"],
    group: "Employee Goals",
    axisGroup: "generic_percentage",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average completion percentage of employee goals.",
    axisLabelOverrideCount: "Avg. Completion (%)",
  },
  AVG_INDUSTRY_SEPARATION_INTENSITY: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_INDUSTRY_SEPARATION_INTENSITY",
    label: "Avg. Industry Exit Rate (Market)",
    columnsReferenced: [
      "industry_context_blended_industry_separation_intensity",
    ],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Intensity",
    description:
      "Exits per job per year for the employer's INDUSTRY in this area. An estimate built from the occupations the industry employs, not a measured industry figure. Never combine it with the role exit rate - they are different yardsticks.",
  },
  AVG_INDUSTRY_TOTAL_POSITIONS: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_INDUSTRY_TOTAL_POSITIONS",
    label: "Avg. Jobs in Industry (Market)",
    columnsReferenced: ["industry_context_industry_total_positions"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Count",
    description:
      "The average number of total positions for the employee's industry in this area, across all roles.",
  },
  AVG_INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY: createLightcastMeasure(
    "AVG_INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY",
    "INDUSTRY_CONTEXT_INDUSTRY_POSITIONS_CHANGE_YOY",
    "average",
    "fraction_percentage",
    "Year-over-year percentage change in the number of local jobs within this industry across all occupations.",
  ),
  AVG_INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION: createLightcastMeasure(
    "AVG_INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION",
    "INDUSTRY_CONTEXT_INDUSTRY_SHARE_OF_OCCUPATION",
    "average",
    "fraction_percentage",
    "Share of all local jobs in this occupation that are located in this industry. The denominator is industry_context_occupation_total_positions.",
  ),
  AVG_INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS: createLightcastMeasure(
    "AVG_INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS",
    "INDUSTRY_CONTEXT_OCCUPATION_TOTAL_POSITIONS",
    "average",
    "market_count",
    "Estimated number of jobs in the area within this occupation across all industries.",
  ),
  AVG_LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO:
    createLightcastMeasure(
      "AVG_LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO",
      "LABOR_AVAILABILITY_CURRENT_LABOR_AVAILABILITY_RATIO",
      "average",
      "intensity",
      "Current unemployed workers divided by current employed workers in the area. Measures the number of unemployed people available relative to employed people; it is not the unemployment rate, which uses the labor force as its denominator.",
    ),
  AVG_LABOR_AVAILABILITY_EMPLOYED_WORKERS: createLightcastMeasure(
    "AVG_LABOR_AVAILABILITY_EMPLOYED_WORKERS",
    "LABOR_AVAILABILITY_EMPLOYED_WORKERS",
    "average",
    "market_count",
    "Estimated number of people in the area who are currently employed.",
  ),
  AVG_LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE:
    createLightcastMeasure(
      "AVG_LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE",
      "LABOR_AVAILABILITY_LABOR_AVAILABILITY_CHANGE_FROM_12M_AVERAGE",
      "average",
      "fraction_percentage",
      "Relative difference between current labor availability and its own trailing-12-month average. Positive values indicate that current availability is higher than usual; negative values indicate that it is lower than usual.",
    ),
  AVG_LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY:
    createLightcastMeasure(
      "AVG_LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY",
      "LABOR_AVAILABILITY_LOCAL_MINUS_NATIONAL_LABOR_AVAILABILITY",
      "average",
      "intensity",
      "Local current labor availability ratio minus the national current labor availability ratio. Positive values indicate more unemployed people relative to employed people locally than nationally.",
    ),
  AVG_LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO:
    createLightcastMeasure(
      "AVG_LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO",
      "LABOR_AVAILABILITY_NATIONAL_CURRENT_LABOR_AVAILABILITY_RATIO",
      "average",
      "intensity",
      "Current unemployed workers divided by current employed workers across the country. Provides the national benchmark for the local labor availability ratio.",
    ),
  AVG_LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS: createLightcastMeasure(
    "AVG_LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS",
    "LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS",
    "average",
    "market_count",
    "Average number of employed people in the area over the trailing 12 months. Smooths monthly or seasonal variation.",
  ),
  AVG_LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO:
    createLightcastMeasure(
      "AVG_LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO",
      "LABOR_AVAILABILITY_TRAILING_12M_LABOR_AVAILABILITY_RATIO",
      "average",
      "intensity",
      "Trailing-12-month average unemployed workers divided by trailing-12-month average employed workers in the area. Measures smoothed labor availability relative to employment; it is not the unemployment rate.",
    ),
  AVG_LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS:
    createLightcastMeasure(
      "AVG_LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS",
      "LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS",
      "average",
      "market_count",
      "Average number of unemployed people in the area over the trailing 12 months. Smooths monthly or seasonal variation.",
    ),
  AVG_LABOR_AVAILABILITY_UNEMPLOYED_WORKERS: createLightcastMeasure(
    "AVG_LABOR_AVAILABILITY_UNEMPLOYED_WORKERS",
    "LABOR_AVAILABILITY_UNEMPLOYED_WORKERS",
    "average",
    "market_count",
    "Estimated number of people in the area who are unemployed and actively looking for work.",
  ),
  AVG_OCCUPATION_MARKET_EMP_CHANGE_YOY: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_EMP_CHANGE_YOY",
    "OCCUPATION_MARKET_EMP_CHANGE_YOY",
    "average",
    "fraction_percentage",
    "Year-over-year percentage change in occupied positions for this occupation in the area. Positive values indicate more local jobs than the previous year; negative values indicate fewer.",
  ),
  AVG_OCCUPATION_MARKET_GROWTH_OPENINGS: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_GROWTH_OPENINGS",
    "OCCUPATION_MARKET_GROWTH_OPENINGS",
    "average",
    "market_count",
    "Estimated number of job openings created by net growth in this occupation in the area during the period. These openings result from an increase in the number of jobs, not from replacing workers who leave.",
  ),
  AVG_OCCUPATION_MARKET_HIRES: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_HIRES",
    "OCCUPATION_MARKET_HIRES",
    "average",
    "market_count",
    "Estimated number of times jobs in this occupation were started in the area during the year. A person may contribute more than one hire.",
  ),
  AVG_OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM",
    "OCCUPATION_MARKET_LOCAL_EARNINGS_PREMIUM",
    "average",
    "fraction_percentage",
    "Estimated difference between local and national earnings for this occupation, expressed as a fraction of national earnings. A value of 0.10 means local earnings are approximately 10% higher than the national benchmark; a negative value means lower earnings.",
  ),
  AVG_OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE",
    "OCCUPATION_MARKET_REPLACEMENT_OPENING_RATE",
    "average",
    "intensity",
    "Estimated replacement openings divided by occupied positions for this occupation in the area. Measures the annual hiring required to replace workers who leave, excluding openings created by net job growth.",
  ),
  AVG_OCCUPATION_MARKET_REPLACEMENT_OPENINGS: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_REPLACEMENT_OPENINGS",
    "OCCUPATION_MARKET_REPLACEMENT_OPENINGS",
    "average",
    "market_count",
    "Estimated number of openings in this occupation required to replace workers who left jobs in the area during the year. These openings maintain the existing number of jobs and exclude openings created by net job growth.",
  ),
  AVG_OCCUPATION_MARKET_RESIDENT_EMPLOYMENT: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_RESIDENT_EMPLOYMENT",
    "OCCUPATION_MARKET_RESIDENT_EMPLOYMENT",
    "average",
    "market_count",
    "Estimated number of people who live in the area and work in this occupation, regardless of where their jobs are located. Unlike occupied positions, this counts resident workers rather than local jobs.",
  ),
  AVG_OCCUPATION_MARKET_SEPARATIONS: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_SEPARATIONS",
    "OCCUPATION_MARKET_SEPARATIONS",
    "average",
    "market_count",
    "Estimated number of times jobs in this occupation ended in the area during the year. Includes all exits, including moves to another job; it is not limited to resignations or permanent exits from employment.",
  ),
  AVG_OCCUPATION_MARKET_YOY_HIRES_CHANGE: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_YOY_HIRES_CHANGE",
    "OCCUPATION_MARKET_YOY_HIRES_CHANGE",
    "average",
    "fraction_percentage",
    "Year-over-year percentage change in hires for this occupation in the area. Measures how local hiring volume changed compared with the previous year.",
  ),
  AVG_OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE",
    "OCCUPATION_MARKET_YOY_REPLACEMENT_OPENINGS_CHANGE",
    "average",
    "fraction_percentage",
    "Year-over-year percentage change in replacement openings for this occupation in the area. Measures how replacement demand changed compared with the previous year.",
  ),
  AVG_OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE: createLightcastMeasure(
    "AVG_OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE",
    "OCCUPATION_MARKET_YOY_SEPARATIONS_CHANGE",
    "average",
    "fraction_percentage",
    "Year-over-year percentage change in separations for this occupation in the area. Measures how the number of job exits changed compared with the previous year.",
  ),
  AVG_SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT: createLightcastMeasure(
    "AVG_SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT",
    "SECTOR_UNEMPLOYMENT_SHARE_OF_AREA_UNEMPLOYMENT",
    "average",
    "fraction_percentage",
    "Share of all unemployed people in the area whose most recent job was in this sector. The denominator is total area unemployment.",
  ),
  AVG_SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE:
    createLightcastMeasure(
      "AVG_SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE",
      "SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE",
      "average",
      "market_count",
      "Average number of unemployed people over the trailing 12 months whose most recent job was in this sector.",
    ),
  AVG_SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY: createLightcastMeasure(
    "AVG_SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY",
    "SECTOR_UNEMPLOYMENT_UNEMPLOYED_CHANGE_YOY",
    "average",
    "fraction_percentage",
    "Year-over-year percentage change in the number of unemployed people in the area whose most recent job was in this sector.",
  ),
  AVG_SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS: createLightcastMeasure(
    "AVG_SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS",
    "SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS",
    "average",
    "market_count",
    "Estimated number of unemployed people in the area whose most recent job was in this sector. This is a subset of total local unemployed workers, classified by previous sector.",
  ),
  AVG_INSIGHTS_CONSCIOUS_BLUE_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_CONSCIOUS_BLUE_ENERGY",
    label: "Avg. Cool Blue",
    columnsReferenced: ["conscious_coolBlueEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Blue scores from the Insights Discovery assessment.",
  },
  AVG_INSIGHTS_CONSCIOUS_GREEN_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_CONSCIOUS_GREEN_ENERGY",
    label: "Avg. Earth Green",
    columnsReferenced: ["conscious_earthGreenEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Green scores from the Insights Discovery assessment.",
  },
  AVG_INSIGHTS_CONSCIOUS_RED_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_CONSCIOUS_RED_ENERGY",
    label: "Avg. Fiery Red",
    columnsReferenced: ["conscious_fieryRedEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Red scores from the Insights Discovery assessment.",
  },
  AVG_INSIGHTS_CONSCIOUS_YELLOW_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_CONSCIOUS_YELLOW_ENERGY",
    label: "Avg. Sunshine Yellow",
    columnsReferenced: ["conscious_sunshineYellowEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Yellow scores from the Insights Discovery assessment.",
  },
  AVG_INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY",
    label: "Avg. Cool Blue (LC)",
    columnsReferenced: ["less_conscious_coolBlueEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Less Conscious Blue scores from the Insights Discovery assessment.",
  },
  AVG_INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY",
    label: "Avg. Earth Green (LC)",
    columnsReferenced: ["less_conscious_earthGreenEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Less Conscious Green scores from the Insights Discovery assessment.",
  },
  AVG_INSIGHTS_LESS_CONSCIOUS_RED_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_LESS_CONSCIOUS_RED_ENERGY",
    label: "Avg. Fiery Red (LC)",
    columnsReferenced: ["less_conscious_fieryRedEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Less Conscious Red scores from the Insights Discovery assessment.",
  },
  AVG_INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "AVG_INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY",
    label: "Avg. Sunshine Yellow (LC)",
    columnsReferenced: ["less_conscious_sunshineYellowEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "The average of Less Conscious Yellow scores from the Insights Discovery assessment.",
  },
  AVG_LAG_TIME_DAYS: {
    datasetNames: ["osha_incidents"],
    measure: "AVG_LAG_TIME_DAYS",
    label: "Avg. Lag Time (Days)",
    columnsReferenced: ["lagTime_days"],
    group: "OSHA Incidents",
    axisGroup: "time_in_days",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average lag time in days between OSHA incident date and when the employer was notified.",
  },
  AVG_MARKET_PRESSURE_ROLE: {
    datasetNames: ["main"],
    measure: "AVG_MARKET_PRESSURE_ROLE",
    label: "Avg. Market Pressure (Role)",
    columnsReferenced: ["mpi_score_soc"],
    group: "Industry",
    axisGroup: "intensity10",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    axisLabelOverrideCount: "Index",
    rollupForLLM: false,
    description:
      "Average index for the employee’s SOC in the employer’s area in the range 0-10; it measures occupational market conditions, not individual employee risk.",
  },
  AVG_MARKET_PRESSURE_MARKET: {
    datasetNames: ["main"],
    measure: "AVG_MARKET_PRESSURE_MARKET",
    label: "Avg. Market Pressure (Market)",
    columnsReferenced: ["mpi_score_geo"],
    group: "Industry",
    axisGroup: "intensity10",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    axisLabelOverrideCount: "Index",
    rollupForLLM: false,
    description:
      "Average of a 0–10 scale comparing local versus national labor supply, hiring, employment growth and wage growth across all occupations for that specific geo_level; higher values indicate greater market pressure",
  },
  AVG_OCCUPATION_SEPARATION_INTENSITY: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_OCCUPATION_SEPARATION_INTENSITY",
    label: "Avg. Role Exit Rate (Market)",
    columnsReferenced: ["occupation_market_separation_intensity"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Average exits per job per year for this ROLE in this area. 0.80 means roughly 80 people left for every 100 such jobs. Counts every exit, not just resignations, and it is the market's rate - not this employer's.",
  },
  AVG_OCCUPATION_LOCAL_HIRE_RATE: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_OCCUPATION_LOCAL_HIRE_RATE",
    label: "Avg. Local Hire Rate",
    columnsReferenced: ["occupation_market_local_hire_rate"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Rate",
    description:
      "The average local hire rate for the employee's occupation, specified as a rate of hires per job per year in the employee's area on a scale of 0 to 1.",
  },
  AVG_OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_OCCUPATION_LOCAL_MINUS_NATIONAL_HIRE_RATE",
    label: "Avg. Local Minus National Hire Rate",
    columnsReferenced: ["occupation_market_local_minus_national_hire_rate"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Rate",
    description:
      "Local hire rate minus national for this employee's role. Positive means this area hires into the role faster than the country does.",
  },
  AVG_OCCUPATION_NATIONAL_HIRE_RATE: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_OCCUPATION_NATIONAL_HIRE_RATE",
    label: "Avg. National Hire Rate",
    columnsReferenced: ["occupation_market_national_hire_rate"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Rate",
    description:
      "The average national hire rate for the employee's occupation, specified as a rate of hires per job per year on a scale of 0 to 1.",
  },
  AVG_OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_OCCUPATION_NET_EMPLOYMENT_GROWTH_RATE",
    label: "Avg. Net Employment Growth Rate",
    columnsReferenced: ["occupation_market_net_employment_growth_rate"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Rate",
    description:
      "The average net employment growth rate for the employee's occupation in this area. Positive means the number of jobs is increasing, negative means it is decreasing.",
  },
  AVG_OCCUPATION_OCCUPIED_POSITIONS: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_OCCUPATION_OCCUPIED_POSITIONS",
    label: "Avg. Jobs in Role (Market)",
    columnsReferenced: ["occupation_market_occupied_positions"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Count",
    description:
      "How many jobs of this kind exist in the area - the size of the local pool, and the denominator under the role rates. A modelled estimate, not a headcount.",
  },
  AVG_PERFORMANCE_RATING: {
    datasetNames: ["main", "culture"],
    measure: "AVG_PERFORMANCE_RATING",
    label: "Avg. Performance Rating",
    columnsReferenced: ["Performance_Rating"],
    group: "Organization",
    axisGroup: "performance_rating",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average performance rating of employees, usually based on annual reviews or evaluations on a scale from 1 to 5.",
  },
  AVG_POSITIONS_IN_INDUSTRY_AND_OCCUPATION: {
    datasetNames: ["main", "lightcast"],
    measure: "AVG_POSITIONS_IN_INDUSTRY_AND_OCCUPATION",
    label: "Avg. Jobs in Industry and Role",
    columnsReferenced: ["industry_context_positions_in_industry_occupation"],
    group: "Industry",
    axisGroup: "intensity",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    axisLabelOverrideCount: "Count",
    description:
      "How many jobs in the area are BOTH in this employer's industry AND this person's occupation, provided both the industry and occupation are specified.",
  },
  AVG_SURVEY_SCORE: {
    datasetNames: ["culture"],
    measure: "AVG_SURVEY_SCORE",
    label: "Avg. Survey Score",
    columnsReferenced: ["answer_value"],
    group: "Culture",
    axisGroup: "survey_score",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: false,
    rollupForLLM: false,
    requiresTimeDimensionOrFilter: false,
    description:
      "Measures the average likert scores of survey responses for culture-related questions, providing insights into employee perceptions and organizational culture.",
  },
  AVG_TIME_TO_FILL: {
    datasetNames: ["recruitment_applications"],
    measure: "AVG_TIME_TO_FILL",
    label: "Avg. Time to Fill (Days)",
    columnsReferenced: ["Time to Fill (Days)"],
    group: "Recruitment",
    axisGroup: "time_in_days",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average number of days taken to fill job requisitions within the organization, from posting to hire date. This is a primary metric of recruiter performance.",
  },
  BENCHMARK_HEADCOUNT: {
    datasetNames: ["benchmark"],
    measure: "BENCHMARK_HEADCOUNT",
    label: "Benchmark",
    columnsReferenced: ["EmployeeCount"],
    group: "Benchmark",
    axisGroup: "benchmark_percentage",
    alwaysShowCount: true,
    showEmployeeCount: false, // Benchmark percentages have distorted counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the employee count from industry benchmark data to compare against the organization's headcount. This measure must be selected if the benchmark dataset is used.",
    disableProjections: true,
  },
  COMPANY_COUNT: {
    datasetNames: ["main", "lightcast"],
    measure: "COMPANY_COUNT",
    label: "Company Count",
    columnsReferenced: ["companyId"],
    group: "Industry",
    axisGroup: "company_count",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description: "Counts the number of companies responding to the query.",
  },
  COMPENSATION_GAP: {
    datasetNames: ["main"],
    measure: "COMPENSATION_GAP",
    label: "Compensation Gap",
    columnsReferenced: ["total_compensation"],
    group: "Organization",
    axisGroup: "compensation_gap",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the delta in compensation between different groups of employees and the mean for the organization, often used to identify pay equity issues within the organization.",
  },
  COMPENSATION_RANGE_TOP: {
    datasetNames: ["main"],
    measure: "COMPENSATION_RANGE_TOP",
    label: "Compensation Range (Top)",
    columnsReferenced: ["total_compensation"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    rollupForLLM: false,
    requiresTimeDimensionOrFilter: false,
    description:
      "Measures the highest compensation values across the selected dimensions or the organization.",
    disableProjections: true,
  },
  COMPENSATION_RANGE_BOTTOM: {
    datasetNames: ["main"],
    measure: "COMPENSATION_RANGE_BOTTOM",
    label: "Compensation Range (Bottom)",
    columnsReferenced: ["total_compensation"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: true,
    rollupForLLM: false,
    requiresTimeDimensionOrFilter: false,
    description:
      "Measures the lowest compensation values across the selected dimensions or the organization.",
    disableProjections: true,
  },
  COST_OF_ATTRITION: {
    datasetNames: ["main"],
    measure: "COST_OF_ATTRITION",
    label: "Cost of Attrition",
    columnsReferenced: ["terminationDate", "total_compensation"],
    group: "Organization",
    axisGroup: "cost_of_attrition",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the total cost incurred by the organization due to employee attrition, including recruitment, training, and lost productivity costs.",
  },
  GOAL_COUNT: {
    datasetNames: ["employee_goals"],
    measure: "GOAL_COUNT",
    label: "Goal Count",
    columnsReferenced: ["employeeGoalType"],
    group: "Employee Goals",
    axisGroup: "goal_count",
    alwaysShowCount: false,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the number of goals set (rows from the goals dataset captured) within the organization, providing insights into employee performance management and goal-setting practices.",
  },
  HEADCOUNT: {
    datasetNames: ["main"],
    measure: "HEADCOUNT",
    label: "Headcount",
    columnsReferenced: [],
    group: "Demographics",
    axisGroup: "headcount",
    alwaysShowCount: false,
    showEmployeeCount: false, // Headcount IS the employee count
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the total number of employees within the organization or specific segments based on applied dimensions and filters.",
  },
  HEADCOUNT_GOALS: {
    datasetNames: ["employee_goals"],
    measure: "HEADCOUNT_GOALS",
    label: "Headcount",
    columnsReferenced: [],
    group: "Employee Goals",
    axisGroup: "headcount",
    alwaysShowCount: false,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the number of employees with goals set within the organization, providing insights into employee engagement and goal-setting practices.",
  },
  HIRES: {
    datasetNames: ["main"],
    measure: "HIRES",
    label: "Hires",
    columnsReferenced: ["hireDate"],
    group: "Organization",
    axisGroup: "hires",
    alwaysShowCount: false,
    showEmployeeCount: false, // Hires IS the count
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: true,
    rollupForLLM: true,
    description:
      "Measures the total number of new employees hired within the organization over a specified time period.",
  },
  HIRES_FROM_APPLICATIONS: {
    datasetNames: ["recruitment_applications"],
    measure: "HIRES_FROM_APPLICATIONS",
    label: "Hires",
    columnsReferenced: ["Hired_Date"],
    group: "Recruitment",
    axisGroup: "hires",
    alwaysShowCount: false,
    showEmployeeCount: false, // Hires IS the count
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the total number of new employees hired from applications within the organization over a specified time period.",
  },
  INCIDENT_COUNT: {
    datasetNames: ["osha_incidents"],
    measure: "INCIDENT_COUNT",
    label: "Incident Count",
    columnsReferenced: ["OSHA Incident Date"],
    group: "OSHA Incidents",
    axisGroup: "incident_count",
    alwaysShowCount: false,
    showEmployeeCount: false, // Incident counts, not employee counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the total number of OSHA incidents within the organization over a specified time period.",
  },
  JOB_REQ_COUNT: {
    datasetNames: ["recruitment_applications"],
    measure: "JOB_REQ_COUNT",
    label: "Job Requisition Count",
    columnsReferenced: ["Requisition ID"],
    group: "Recruitment",
    axisGroup: "job_req_count",
    alwaysShowCount: true,
    showEmployeeCount: false, // Job Requisition counts, not employee counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the total number of job requisitions within the org, providing insights into hiring demand and recruitment activity.",
  },
  JOB_REQ_ACCEPTANCE_RATE: {
    datasetNames: ["recruitment_applications"],
    measure: "JOB_REQ_ACCEPTANCE_RATE",
    label: "Job Req Acceptance Rate",
    columnsReferenced: ["Acceptance Rate"],
    group: "Recruitment",
    axisGroup: "generic_percentage",
    alwaysShowCount: true,
    showEmployeeCount: false, // Job Requisition acceptance rates, not employee counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description: "Measures acceptance rate for offers made to candidates.",
    axisLabelOverrideCount: "Job Req Acceptance Rate (%)",
  },
  PROMOTIONS: {
    datasetNames: ["main"],
    measure: "PROMOTIONS",
    label: "Promotions",
    columnsReferenced: ["Date of Promotion"],
    group: "Organization",
    axisGroup: "promotions",
    alwaysShowCount: false,
    showEmployeeCount: false, // Promotions IS the count
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: true,
    rollupForLLM: true,
    description:
      "Measures the total number of employee promotions within the organization over a specified time period.",
  },
  RETENTION_RATE: {
    datasetNames: ["main"],
    measure: "RETENTION_RATE",
    label: "Retention Rate",
    columnsReferenced: ["terminationDate"],
    group: "Organization",
    axisGroup: "generic_percentage",
    alwaysShowCount: true,
    showEmployeeCount: false, // Rate metrics have distorted employee counts
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the percentage of employees who remain with the organization over a specified time period, indicating employee stability and satisfaction.",
    axisLabelOverrideCount: "Retention Rate (%)",
  },
  SURVEY_SCORE: {
    datasetNames: ["culture"],
    measure: "SURVEY_SCORE",
    label: "Survey Score",
    columnsReferenced: ["answer_value"],
    group: "Culture",
    axisGroup: "number_of_respondents",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    preferChartType: "Horizontal Stacked Bar",
    description:
      "Measures the number of respondents giving each Likert survey score for culture-related questions, providing insights into employee perceptions and organizational culture.",
  },
  TERMINATIONS: {
    datasetNames: ["main"],
    measure: "TERMINATIONS",
    label: "Terminations",
    columnsReferenced: ["terminationDate"],
    group: "Organization",
    axisGroup: "terminations",
    alwaysShowCount: false,
    showEmployeeCount: false, // Terminations IS the count
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: true,
    rollupForLLM: true,
    description:
      "Measures the total number of employee terminations within the organization over a specified time period, including voluntary and involuntary separations.",
  },
  TIME_IN_STAGE: {
    datasetNames: ["recruitment_applications"],
    measure: "TIME_IN_STAGE",
    label: "Avg. Time in Stage",
    columnsReferenced: ["Current_Stage"],
    group: "Recruitment",
    axisGroup: "time_in_days",
    alwaysShowCount: false,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average number of days that applications spend in each stage of the recruitment pipeline, providing insights into bottlenecks and process efficiency.",
  },
  TIME_TO_FILL_DRIVERS: {
    datasetNames: ["time_to_fill_drivers"],
    measure: "TIME_TO_FILL_DRIVERS",
    label: "Top Drivers of Longer Time to Fill",
    columnsReferenced: ["impact"],
    group: "Recruitment",
    axisGroup: "importance",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the importance score of various factors contributing to time to fill on a scale of -1 to 1.",
    disableDimensions: true,
    disableProjections: true,
    preferChartType: "Bar",
  },
  TIME_TO_HIRE_DAYS: {
    datasetNames: ["recruitment_applications"],
    measure: "TIME_TO_HIRE_DAYS",
    label: "Avg. Time to Hire (Days)",
    columnsReferenced: ["Hired_Date", "Applied_Date"],
    group: "Recruitment",
    axisGroup: "time_in_days",
    alwaysShowCount: true,
    showEmployeeCount: false,
    requiresActiveEmployeeFilter: false,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the average number of days taken to hire candidates from applications within the organization, from application submission to hire date.",
  },
  TOTAL_ANNUALIZED_TOTAL_CASH_COMPENSATION: {
    datasetNames: ["main"],
    measure: "TOTAL_ANNUALIZED_TOTAL_CASH_COMPENSATION",
    label: "Total Annualized Cash Compensation",
    columnsReferenced: ["annualized_total_cash_compensation"],
    group: "Organization",
    axisGroup: "total_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the total annualized cash compensation of employees within the organization, including salary and bonuses.",
  },
  TOTAL_BONUS_AMOUNT: {
    datasetNames: ["main"],
    measure: "TOTAL_BONUS_AMOUNT",
    label: "Total Bonuses",
    columnsReferenced: ["bonusAmount"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    isTransactional: true,
    rollupForLLM: true,
    description:
      "Measures the total bonus amount paid to employees within the organization, providing insights into variable compensation trends.",
  },
  TOTAL_COMMISSION_AMOUNT: {
    datasetNames: ["main"],
    measure: "TOTAL_COMMISSION_AMOUNT",
    label: "Total Commissions",
    columnsReferenced: ["commissionAmount"],
    group: "Organization",
    axisGroup: "avg_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    isTransactional: true,
    rollupForLLM: true,
    description:
      "Measures the total commission amount paid to employees within the organization, providing insights into variable compensation trends for sales roles.",
  },
  TOTAL_LABOR_AVAILABILITY_EMPLOYED_WORKERS: createLightcastMeasure(
    "TOTAL_LABOR_AVAILABILITY_EMPLOYED_WORKERS",
    "LABOR_AVAILABILITY_EMPLOYED_WORKERS",
    "total",
    "market_count",
    "Estimated number of people in the area who are currently employed.",
  ),
  TOTAL_LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS:
    createLightcastMeasure(
      "TOTAL_LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS",
      "LABOR_AVAILABILITY_TRAILING_12M_EMPLOYED_WORKERS",
      "total",
      "market_count",
      "Average number of employed people in the area over the trailing 12 months. Smooths monthly or seasonal variation.",
    ),
  TOTAL_LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS:
    createLightcastMeasure(
      "TOTAL_LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS",
      "LABOR_AVAILABILITY_TRAILING_12M_UNEMPLOYED_WORKERS",
      "total",
      "market_count",
      "Average number of unemployed people in the area over the trailing 12 months. Smooths monthly or seasonal variation.",
    ),
  TOTAL_LABOR_AVAILABILITY_UNEMPLOYED_WORKERS: createLightcastMeasure(
    "TOTAL_LABOR_AVAILABILITY_UNEMPLOYED_WORKERS",
    "LABOR_AVAILABILITY_UNEMPLOYED_WORKERS",
    "total",
    "market_count",
    "Estimated number of people in the area who are unemployed and actively looking for work.",
  ),
  TOTAL_OCCUPATION_MARKET_RESIDENT_EMPLOYMENT: createLightcastMeasure(
    "TOTAL_OCCUPATION_MARKET_RESIDENT_EMPLOYMENT",
    "OCCUPATION_MARKET_RESIDENT_EMPLOYMENT",
    "total",
    "market_count",
    "Estimated number of people who live in the area and work in this occupation, regardless of where their jobs are located. Unlike occupied positions, this counts resident workers rather than local jobs.",
  ),
  TOTAL_SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE:
    createLightcastMeasure(
      "TOTAL_SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE",
      "SECTOR_UNEMPLOYMENT_TRAILING_12M_UNEMPLOYED_WORKERS_AVERAGE",
      "total",
      "market_count",
      "Average number of unemployed people over the trailing 12 months whose most recent job was in this sector.",
    ),
  TOTAL_SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS: createLightcastMeasure(
    "TOTAL_SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS",
    "SECTOR_UNEMPLOYMENT_UNEMPLOYED_WORKERS",
    "total",
    "market_count",
    "Estimated number of unemployed people in the area whose most recent job was in this sector. This is a subset of total local unemployed workers, classified by previous sector.",
  ),
  TOTAL_INSIGHTS_CONSCIOUS_BLUE_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_CONSCIOUS_BLUE_ENERGY",
    label: "Total Cool Blue",
    columnsReferenced: ["conscious_coolBlueEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Conscious Blue scores from the Insights Discovery assessment.",
  },
  TOTAL_INSIGHTS_CONSCIOUS_GREEN_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_CONSCIOUS_GREEN_ENERGY",
    label: "Total Earth Green",
    columnsReferenced: ["conscious_earthGreenEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Conscious Green scores from the Insights Discovery assessment.",
  },
  TOTAL_INSIGHTS_CONSCIOUS_RED_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_CONSCIOUS_RED_ENERGY",
    label: "Total Fiery Red",
    columnsReferenced: ["conscious_fieryRedEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Conscious Red scores from the Insights Discovery assessment.",
  },
  TOTAL_INSIGHTS_CONSCIOUS_YELLOW_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_CONSCIOUS_YELLOW_ENERGY",
    label: "Total Sunshine Yellow",
    columnsReferenced: ["conscious_sunshineYellowEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Conscious Yellow scores from the Insights Discovery assessment.",
  },
  TOTAL_INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_LESS_CONSCIOUS_BLUE_ENERGY",
    label: "Total Cool Blue (LC)",
    columnsReferenced: ["less_conscious_coolBlueEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Less Conscious Blue scores from the Insights Discovery assessment.",
  },
  TOTAL_INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_LESS_CONSCIOUS_GREEN_ENERGY",
    label: "Total Earth Green (LC)",
    columnsReferenced: ["less_conscious_earthGreenEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Less Conscious Green scores from the Insights Discovery assessment.",
  },
  TOTAL_INSIGHTS_LESS_CONSCIOUS_RED_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_LESS_CONSCIOUS_RED_ENERGY",
    label: "Total Fiery Red (LC)",
    columnsReferenced: ["less_conscious_fieryRedEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Less Conscious Red scores from the Insights Discovery assessment.",
  },
  TOTAL_INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY: {
    datasetNames: ["main", "culture"],
    measure: "TOTAL_INSIGHTS_LESS_CONSCIOUS_YELLOW_ENERGY",
    label: "Total Sunshine Yellow (LC)",
    columnsReferenced: ["less_conscious_sunshineYellowEnergy"],
    group: "Insights Discovery",
    axisGroup: "color_energy",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: false,
    description:
      "Measures the SUM of Less Conscious Yellow scores from the Insights Discovery assessment.",
  },
  TOTAL_COMPENSATION: {
    datasetNames: ["main"],
    measure: "TOTAL_COMPENSATION",
    label: "Total Compensation",
    columnsReferenced: ["total_compensation"],
    group: "Organization",
    axisGroup: "total_compensation",
    alwaysShowCount: true,
    showEmployeeCount: true,
    requiresActiveEmployeeFilter: true,
    requiresTimeDimensionOrFilter: false,
    rollupForLLM: true,
    description:
      "Measures the total compensation paid to employees, including salary, bonuses, and benefits, within the organization or specific segments.",
  },
};
